import { Button, ButtonGroup, DeleteIcon, DropdownMenu, EditIcon, ImportIcon, Table, Tooltip } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useIsUsedInLesson } from '../../../hooks/useIsUsedInLesson';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { UsedInLessonsTooltipContent } from '../../TooltipContents/LessonUsedInTooltipContent/UsedInLessonsTooltipContent';
import { useClassesTable } from '../hooks/useClassesTable';
import { ClassesTableType } from '../types/ClassesTypes';

type TimetableClassesTableProps = {
  loading: boolean;
  onAdd: () => void;
  onInherit: () => void;
  onEdit: (uuid: string) => void;
  onAddColor: (uuids: string[]) => void;
  onDelete: (uuids: string[]) => void;
  readonly: boolean;
};

export const TimetableClassesTable = observer(
  ({ loading, onAdd, onInherit, onEdit, onAddColor, onDelete, readonly }: TimetableClassesTableProps) => {
    const { t } = useTranslation();
    const currentSchoolYear = useUserConfigContext().selectedSchoolYear;

    const check = useIsUsedInLesson();

    const {
      data,
      columns,
      tableSettings,
      tableSorting,
      tableVisibility,
      tableSelection,
      emptyStateSettings,
      printerSettings,
      actionBarSettings,
    } = useClassesTable({
      context: 'timetable',
      columns: [
        'name',
        'shortName',
        'grade',
        'gradeGroup',
        'tutor1',
        'tutor2',
        'tutor3',
        'room',
        'printOrder',
        'printPageNr',
        'relevantForSubstitution',
        'color',
      ],
    });

    return (
      <Table<ClassesTableType>
        loading={loading}
        columns={columns}
        data={data}
        {...tableSettings}
        {...tableSorting}
        {...tableVisibility}
        {...tableSelection}
        printerSettings={printerSettings}
        actionBarSettings={{
          ...actionBarSettings,
          disableAddButton: readonly || !currentSchoolYear,
          showBulkEdit: true,
          disableBulkEdit: readonly,
          extendedActionsRight: (
            <Button hierarchy='tertiary' icon={<ImportIcon />} onClick={onInherit} disabled={readonly}>
              {t('common.inherit')}
            </Button>
          ),
        }}
        bulkEditDropdownContent={(rows) => {
          return (
            <DropdownMenu
              data={[
                {
                  label: t('common.addRandomColor'),
                  onClick: () => {
                    onAddColor(rows.map(({ original }) => original.uuid));
                    tableSelection.clearSelection();
                  },
                },
                { type: 'ruler' },
                {
                  label: t('common.delete'),
                  color: 'error',
                  onClick: () => {
                    onDelete(
                      rows
                        .filter(({ original }) => !check(original.uuid, 'class').isUsed)
                        .map(({ original }) => original.uuid),
                    );
                    tableSelection.clearSelection();
                  },
                },
              ]}
            />
          );
        }}
        emptyStateSettings={{
          ...emptyStateSettings,
          onButtonClick: onInherit,
        }}
        onAddClick={onAdd}
        lastCol={(row) => {
          const used = check(row.original.uuid, 'class');
          const deleteButton = (
            <Button
              disabled={readonly || used.isUsed}
              hierarchy='secondary'
              type='button'
              icon={<DeleteIcon className='small' color='var(--color-error)' />}
              onClick={() => onDelete([row.original.uuid])}
            />
          );
          return (
            <ButtonGroup disabled={readonly}>
              <Button
                hierarchy='secondary'
                type='button'
                onClick={() => onEdit(row.original.uuid)}
                icon={<EditIcon className='small' />}
                disabled={readonly}
              />
              {used.isUsed ? (
                <Tooltip
                  content={
                    <UsedInLessonsTooltipContent lessons={used.lessons} usedInText={t('classes.usedInLesson')} />
                  }
                >
                  {deleteButton}
                </Tooltip>
              ) : (
                deleteButton
              )}
            </ButtonGroup>
          );
        }}
      />
    );
  },
);
